import {useState, useEffect} from "react"
import {Drawer, Box, Typography, TextField, Button, Divider, useMediaQuery, CircularProgress, Chip} from "@mui/material"
import {useTheme} from "@mui/material/styles"
import {PersonAdd as PersonAddIcon} from "@mui/icons-material"
import {useTranslation} from "react-i18next"
import ChangePassword from "./change-password"
import AddUser from "./add-user"
import axiosInstance from "../config/axios-config";
import i18n from "../i18n/i18n";
import GlowingMessage from "../errors/glowing-message";

// Role displayed with a friendly color
const roleColor = (role) => {
    switch (role) {
        case "ADMIN":
            return "error"
        case "OPERATOR":
            return "primary"
        case "COURIER":
            return "success"
        default:
            return "default"
    }
}

const AccountManagement = ({open, onClose, email, tenantName}) => {
    const {t} = useTranslation()
    const usTheme = useTheme()
    const isSmallScreen = useMediaQuery(usTheme.breakpoints.down("sm"))

    const [user, setUser] = useState(null);

    const [loading, setLoading] = useState(false)
    const [saving, setSaving] = useState(false)
    const [passwordDialogOpen, setPasswordDialogOpen] = useState(false)
    const [addUserDialogOpen, setAddUserDialogOpen] = useState(false)
    const [message, setMessage] = useState("")

    const isAdmin = user?.role === "ADMIN"

    useEffect(() => {
        if (open) {
            fetchUserData(email).then(r => console.log("User data fetched"))
        }
    }, [open])

    const fetchUserData = async (email) => {
        setLoading(true)
        try {
            const response = await axiosInstance.get("/api/v1/users", {
                params: {
                    email: email,
                },
                headers: {
                    "Language-Context": i18n.language.toUpperCase() || "EN",
                },
            })
            setUser(response.data)
        } catch (error) {
            console.error("Error fetching user data:", error)
        } finally {
            setLoading(false)
        }
    }

    const handleSaveChanges = async () => {
        setSaving(true)
        const updatedUser = {
            ...user,
            id: null,
            password: null
        }
        try {
            const response = await axiosInstance.put(`/api/v1/users/${user.id}`, updatedUser, {
                headers: {
                    "Language-Context": i18n.language.toUpperCase() || "EN",
                }
            })
            setUser(response.data.data)
            setMessage(response.data?.message || "User updated successfully")
        } catch (error) {
            console.error("Error saving changes:", error)
            alert(t("accountManagement.saveFailed") || "Failed to save changes")
        } finally {
            setSaving(false)
        }
    }

    const handleChangePassword = () => {
        setPasswordDialogOpen(true)
    }

    const handleUserCreated = () => {
        setMessage(t("UserCreatedSuccessfully") || "User created successfully")
    }


    return (
        <>
            <GlowingMessage message={message} onClose={() => setMessage("")}/>
            <Drawer
                anchor="right"
                open={open}
                onClose={onClose}
                sx={{
                    "& .MuiDrawer-paper": {
                        width: isSmallScreen ? "100%" : 420,
                        display: "flex",
                        height: "100%",
                        marginTop: "70px",
                    },
                }}
            >
                <Box sx={{flexGrow: 1, padding: "16px", width: isSmallScreen ? "100%" : 380, height: "100vh"}}>
                    <Box sx={{display: "flex", alignItems: "center", justifyContent: "space-between", gap: 1}}>
                        <Typography variant="h6" gutterBottom>
                            {user?.name || ""} {user?.surname || ""}
                        </Typography>
                        {user?.role && (
                            <Chip
                                label={t(user.role) || user.role}
                                color={roleColor(user.role)}
                                size="small"
                                sx={{fontWeight: 600}}
                            />
                        )}
                    </Box>
                    <Typography
                        variant="body2"
                        color="text.secondary"
                        sx={{
                            lineHeight: 1.2,
                            maxWidth: 220,
                            whiteSpace: "nowrap",
                            overflow: "hidden",
                            textOverflow: "ellipsis",
                            mb: 0.5,
                        }}
                    >
                        {tenantName}
                    </Typography>
                    <Divider/>

                    {loading ? (
                        <Box sx={{display: "flex", justifyContent: "center", mt: 4}}>
                            <CircularProgress/>
                        </Box>
                    ) : (
                        <Box component="form" sx={{mt: 2}}>
                            <TextField
                                label={t("FirstName")}
                                fullWidth
                                margin="normal"
                                variant="outlined"
                                value={user?.name || ""}
                                onChange={(e) =>
                                    setUser((prev) => ({ ...prev, name: e.target.value }))}
                            />
                            <TextField
                                label={t("LastName")}
                                fullWidth
                                margin="normal"
                                variant="outlined"
                                value={user?.surname || ""}
                                onChange={(e) => setUser((prev) => ({ ...prev, surname: e.target.value }))}
                            />
                            <TextField
                                label={t("Email")}
                                fullWidth
                                margin="normal"
                                variant="outlined"
                                value={user?.email}
                                disabled
                                helperText={t("EmailCannotBeChanged")}
                            />
                            <Box
                                sx={{
                                    display: "flex",
                                    flexDirection: "column",
                                    gap: 2,
                                    mt: 3,
                                }}
                            >
                                <Button variant="contained" color="primary" onClick={handleSaveChanges}
                                        disabled={saving} fullWidth>
                                    {saving
                                        ? t("Saving")
                                        : t("SaveChanges")}
                                </Button>
                                <Button variant="outlined" color="primary" onClick={handleChangePassword} fullWidth>
                                    {t("ChangePassword")}
                                </Button>

                                {isAdmin && (
                                    <>
                                        <Divider sx={{my: 1}}>
                                            <Typography variant="caption" color="text.secondary">
                                                {t("Administration") || "Administration"}
                                            </Typography>
                                        </Divider>
                                        <Button
                                            variant="contained"
                                            color="secondary"
                                            startIcon={<PersonAddIcon/>}
                                            onClick={() => setAddUserDialogOpen(true)}
                                            fullWidth
                                        >
                                            {t("AddUser") || "Add User"}
                                        </Button>
                                    </>
                                )}
                            </Box>
                        </Box>
                    )}
                </Box>
            </Drawer>

            <ChangePassword open={passwordDialogOpen} onClose={() => setPasswordDialogOpen(false)} userId={user?.id}/>

            {isAdmin && (
                <AddUser
                    open={addUserDialogOpen}
                    onClose={() => setAddUserDialogOpen(false)}
                    onUserCreated={handleUserCreated}
                />
            )}
        </>
    )
}

export default AccountManagement
